import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Home from "@/pages/Home";
import Finance from "@/pages/Finance";
import KnowledgeBase from "@/pages/KnowledgeBase";
import ArticleDetail from "@/pages/ArticleDetail";
import PatientQuestions from "@/pages/PatientQuestions";
import About from "@/pages/About";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Enquiry from "@/pages/Enquiry";
import Book from "@/pages/Book";
import BookingSuccess from "@/pages/BookingSuccess";
import CancellationPolicy from "@/pages/CancellationPolicy";
import NotFound from "@/pages/NotFound";
import InvisalignActon from "@/pages/services/InvisalignActon";
import DentistActon from "@/pages/services/DentistActon";
import CompositeBondingActon from "@/pages/services/CompositeBondingActon";
import EmergencyDentistActon from "@/pages/services/EmergencyDentistActon";
import CosmeticDentistActon from "@/pages/services/CosmeticDentistActon";
import ImplantsActon from "@/pages/services/ImplantsActon";
import Reviews from "@/pages/Reviews";
import BeforeAfter from "@/pages/BeforeAfter";
import SedationPage from "@/components/SedationPage";
import AreaPage from "@/components/AreaPage";
import ServicePage from "@/components/ServicePage";
import TreatmentAreaPage from "@/components/TreatmentAreaPage";
import FloatingEdgeTabs from "@/components/FloatingEdgeTabs";
import FaviconSwitcher from "@/components/FaviconSwitcher";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import AdminToggle from "@/components/AdminToggle";
import { getAreaSlugs } from "@/data/areas";
import { getAllServiceSlugs } from "@/data/services";
import { getTreatmentAreaCombos } from "@/data/treatmentAreas";

// Admin bundle is code-split so public pages ship a smaller JS payload
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Contacts = lazy(() => import("@/pages/admin/Contacts"));
const Referrals = lazy(() => import("@/pages/admin/Referrals"));
const ContentCreator = lazy(() => import("@/pages/admin/ContentCreator"));
const Newsletter = lazy(() => import("@/pages/admin/Newsletter"));
const Analytics = lazy(() => import("@/pages/admin/Analytics"));
const Tasks = lazy(() => import("@/pages/admin/Tasks"));
const Settings = lazy(() => import("@/pages/admin/Settings"));
const PublishingSchedule = lazy(() => import("@/pages/admin/PublishingSchedule"));
const WorkLog = lazy(() => import("@/pages/admin/WorkLog"));
const AdminBooking = lazy(() => import("@/pages/admin/Booking"));
const AdminGallery = lazy(() => import("@/pages/admin/Gallery"));
const MyCv = lazy(() => import("@/pages/admin/MyCv"));
const SharedCv = lazy(() => import("@/pages/SharedCv"));

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-chocolate-malt" data-testid="admin-loading">Loading…</div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <FaviconSwitcher />
        <FloatingEdgeTabs />
        <ExitIntentPopup />
        <AdminToggle />
        <Suspense fallback={<AdminFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/patient-questions" element={<PatientQuestions />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about-dr-kartik-patel" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/enquiry" element={<Enquiry />} />
            <Route path="/book" element={<Book />} />            <Route path="/payment/success" element={<BookingSuccess />} />
            <Route path="/payment/cancel" element={<Book />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route path="/knowledge-base/:slug" element={<ArticleDetail />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/before-after" element={<BeforeAfter />} />

            <Route path="/dentist-acton" element={<DentistActon />} />
            <Route path="/invisalign-acton" element={<InvisalignActon />} />
            <Route path="/composite-bonding-acton" element={<CompositeBondingActon />} />
            <Route path="/emergency-dentist-acton" element={<EmergencyDentistActon />} />
            <Route path="/cosmetic-dentist-acton" element={<CosmeticDentistActon />} />
            <Route path="/implants-acton" element={<ImplantsActon />} />

            {/* IV sedation cluster — hub + three treatment pages */}
            <Route path="/iv-sedation-dentistry" element={<SedationPage pageKey="hub" />} />
            <Route path="/iv-sedation-wisdom-teeth-removal" element={<SedationPage pageKey="wisdom" />} />
            <Route path="/iv-sedation-dental-implants" element={<SedationPage pageKey="implants" />} />
            <Route path="/iv-sedation-dental-phobia" element={<SedationPage pageKey="phobia" />} />

            {/* Neighbourhood landing pages — generated from data/areas.js */}
            {getAreaSlugs().map((slug) => (
              <Route key={slug} path={`/dentist-${slug}`} element={<AreaPage />} />
            ))}

            {/* Service/condition pages — generated from data/services.js */}
            {getAllServiceSlugs().map((slug) => (
              <Route key={slug} path={`/services/${slug}`} element={<ServicePage />} />
            ))}

            {/* Treatment × area pages (e.g. /invisalign-ealing) — generated from data/treatmentAreas.js */}
            {getTreatmentAreaCombos().map(({ path }) => (
              <Route key={path} path={path} element={<TreatmentAreaPage />} />
            ))}

            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/contacts" element={<Contacts />} />
            <Route path="/admin/referrals" element={<Referrals />} />
            <Route path="/admin/content" element={<ContentCreator />} />
            <Route path="/admin/articles" element={<Navigate to="/admin/content" replace />} />
            <Route path="/admin/topics" element={<Navigate to="/admin/content" replace />} />
            <Route path="/admin/research" element={<Navigate to="/admin/content" replace />} />
            <Route path="/admin/newsletter" element={<Newsletter />} />
            <Route path="/admin/analytics" element={<Analytics />} />
            <Route path="/admin/tasks" element={<Tasks />} />
            <Route path="/admin/settings" element={<Settings />} />
            <Route path="/admin/schedule" element={<PublishingSchedule />} />
            <Route path="/admin/worklog" element={<WorkLog />} />
            <Route path="/admin/booking" element={<AdminBooking />} />
            <Route path="/admin/gallery" element={<AdminGallery />} />
            <Route path="/admin/cv" element={<MyCv />} />

            {/* Token-gated public CV — no site chrome, noindex, expires */}
            <Route path="/cv/:token" element={<SharedCv />} />

            {/* Real 404 (noindex) instead of a soft-404 redirect to "/" */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
